import { useState, useRef } from "react";
import "./SalarySlipCalculator.css";
import Logo from "../../../../assets/logo.png";

const MONTH_OPTIONS = (() => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-IN", { month: "long", year: "numeric" });
    opts.push({ val, label });
  }
  return opts;
})();

function fmt(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function useAnimatedCount(target, duration = 800) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  const animate = (to) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(ease * to));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  return [display, animate];
}

function InputField({ label, id, value, onChange, type = "number", placeholder = "0" }) {
  return (
    <div className="th-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
    </div>
  );
}

function LineItem({ label, amount, isDeduction = false }) {
  if (!amount) return null;
  return (
    <div className={`th-line-item${isDeduction ? " deduction" : ""}`}>
      <span className="th-li-label">{label}</span>
      <span className="th-li-val">{fmt(amount)}</span>
    </div>
  );
}

export default function SalarySlipCalculator() {
  const [form, setForm] = useState({
    empName: "", empId: "", designation: "", department: "",
    payPeriod: MONTH_OPTIONS[0].val,
    basic: "", hra: "", special: "", transport: "", medical: "", otherEarn: "",
    pf: "", pt: "", esi: "", tds: "", otherDed: "",
  });
  const [result, setResult] = useState(null);
  const [netDisplay, animateNet] = useAnimatedCount(0);
  const slipRef = useRef(null);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));
  const n = (key) => parseFloat(form[key]) || 0;

  const generate = () => {
    const gross = n("basic") + n("hra") + n("special") + n("transport") + n("medical") + n("otherEarn");
    const totalDed = n("pf") + n("pt") + n("esi") + n("tds") + n("otherDed");
    const net = gross - totalDed;

    const periodLabel = MONTH_OPTIONS.find((o) => o.val === form.payPeriod)?.label || form.payPeriod;
    const slipNo = "TH-" + Math.floor(10000 + Math.random() * 90000);

    setResult({
      ...form, gross, totalDed, net, periodLabel, slipNo,
      basic: n("basic"), hra: n("hra"), special: n("special"),
      transport: n("transport"), medical: n("medical"), otherEarn: n("otherEarn"),
      pf: n("pf"), pt: n("pt"), esi: n("esi"), tds: n("tds"), otherDed: n("otherDed"),
    });
    animateNet(net);

    setTimeout(() => {
      slipRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const handlePrint = () => window.print();

  return (
    <div className="th-root">
      {/* BG decoration */}
      <div className="th-bg-orb th-orb1" />
      <div className="th-bg-orb th-orb2" />
      <div className="th-grid-overlay" />

      <div className="th-wrapper">
        {/* Page title */}
        <div className="th-page-title">
          <h1>Salary Slip <span>Calculator</span></h1>
          <p>Generate accurate, professional pay slips for your team instantly.</p>
        </div>

        {/* Form Card */}
        <div className="th-card">
          <div className="th-card-bar">
            <span className="th-dot d1" /><span className="th-dot d2" /><span className="th-dot d3" />
            <span className="th-card-title">Employee & Payroll Details</span>
          </div>

          <div className="th-card-body">
            {/* Employee info */}
            <div className="th-section-label">Employee Info</div>
            <div className="th-grid-2">
              <InputField label="Full Name" id="empName" value={form.empName} onChange={set("empName")} type="text" placeholder="e.g. Arjun Mehta" />
              <InputField label="Employee ID" id="empId" value={form.empId} onChange={set("empId")} type="text" placeholder="e.g. TH-0042" />
            </div>
            <div className="th-grid-3 mt16">
              <InputField label="Designation" id="designation" value={form.designation} onChange={set("designation")} type="text" placeholder="e.g. Product Manager" />
              <InputField label="Department" id="department" value={form.department} onChange={set("department")} type="text" placeholder="e.g. Engineering" />
              <div className="th-field">
                <label htmlFor="payPeriod">Pay Period</label>
                <select id="payPeriod" value={form.payPeriod} onChange={(e) => set("payPeriod")(e.target.value)}>
                  {MONTH_OPTIONS.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div className="th-divider" />

            {/* Earnings */}
            <div className="th-section-label">Earnings</div>
            <div className="th-grid-3">
              <InputField label="Basic Salary (₹)" id="basic" value={form.basic} onChange={set("basic")} />
              <InputField label="HRA (₹)" id="hra" value={form.hra} onChange={set("hra")} />
              <InputField label="Special Allowance (₹)" id="special" value={form.special} onChange={set("special")} />
            </div>
            <div className="th-grid-3 mt16">
              <InputField label="Transport Allowance (₹)" id="transport" value={form.transport} onChange={set("transport")} />
              <InputField label="Medical Allowance (₹)" id="medical" value={form.medical} onChange={set("medical")} />
              <InputField label="Other Earnings (₹)" id="otherEarn" value={form.otherEarn} onChange={set("otherEarn")} />
            </div>

            <div className="th-divider" />

            {/* Deductions */}
            <div className="th-section-label">Deductions</div>
            <div className="th-ded-grid">
              {[
                { label: "Provident Fund (PF)", key: "pf" },
                { label: "Professional Tax", key: "pt" },
                { label: "ESI", key: "esi" },
                { label: "TDS / Income Tax", key: "tds" },
                { label: "Other Deductions", key: "otherDed" },
              ].map(({ label, key }) => (
                <div className="th-ded-row" key={key}>
                  <span className="th-ded-label">{label}</span>
                  <input
                    type="number"
                    value={form[key]}
                    onChange={(e) => set(key)(e.target.value)}
                    placeholder="0"
                  />
                </div>
              ))}
            </div>

            <div className="th-cta-row">
              <button className="th-btn-generate" onClick={generate}>
                <span>Generate Salary Slip</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* RESULT SLIP */}
        {result && (
          <div className="th-result" ref={slipRef}>
            <div className="th-slip" id="th-slip-print">
              {/* Slip Header */}
              <div className="th-slip-header">
                <div className="th-slip-company">
                  <div className="th-slip-co">
                    <img
                      src={Logo}
                      alt="Talent Hive"
                      className="th-slip-logo-img"
                    />
                    <div className="th-slip-co-sub">
                      Human Resources & Payroll
                    </div>
                  </div>
                </div>
                <div className="th-slip-meta">
                  <div className="th-slip-period">{result.periodLabel}</div>
                  <div className="th-slip-no">Slip #{result.slipNo}</div>
                </div>
              </div>

              {/* Employee Info Bar */}
              <div className="th-slip-emp">
                {[
                  { l: "Employee Name", v: result.empName || "—" },
                  { l: "Employee ID", v: result.empId || "—" },
                  { l: "Designation", v: result.designation || "—" },
                  { l: "Department", v: result.department || "—" },
                  { l: "Pay Days", v: "31 Days" },
                  { l: "Pay Mode", v: "Bank Transfer" },
                ].map(({ l, v }) => (
                  <div className="th-emp-cell" key={l}>
                    <span className="th-emp-label">{l}</span>
                    <span className="th-emp-val">{v}</span>
                  </div>
                ))}
              </div>

              {/* Earnings & Deductions */}
              <div className="th-slip-body">
                <div className="th-slip-col">
                  <div className="th-col-head">Earnings</div>
                  <LineItem label="Basic Salary" amount={result.basic} />
                  <LineItem label="House Rent Allowance" amount={result.hra} />
                  <LineItem label="Special Allowance" amount={result.special} />
                  <LineItem label="Transport Allowance" amount={result.transport} />
                  <LineItem label="Medical Allowance" amount={result.medical} />
                  <LineItem label="Other Earnings" amount={result.otherEarn} />
                  {!result.gross && <div className="th-empty">No earnings added</div>}
                </div>
                <div className="th-slip-divider" />
                <div className="th-slip-col">
                  <div className="th-col-head">Deductions</div>
                  <LineItem label="Provident Fund" amount={result.pf} isDeduction />
                  <LineItem label="Professional Tax" amount={result.pt} isDeduction />
                  <LineItem label="ESI" amount={result.esi} isDeduction />
                  <LineItem label="TDS / Income Tax" amount={result.tds} isDeduction />
                  <LineItem label="Other Deductions" amount={result.otherDed} isDeduction />
                  {!result.totalDed && <div className="th-empty">No deductions</div>}
                </div>
              </div>

              {/* Totals */}
              <div className="th-slip-totals">
                <div className="th-total-block">
                  <span className="th-total-label">Gross Earnings</span>
                  <span className="th-total-amt">{fmt(result.gross)}</span>
                </div>
                <div className="th-total-block th-total-main">
                  <span className="th-total-label">Net Take Home</span>
                  <span className="th-total-amt">₹{netDisplay.toLocaleString("en-IN")}</span>
                </div>
                <div className="th-total-block">
                  <span className="th-total-label">Total Deductions</span>
                  <span className="th-total-amt">{fmt(result.totalDed)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="th-actions">
              <button className="th-btn-outline" onClick={handlePrint}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M4 4V2h7v2M4 11H2V6h11v5h-2m-5 0v3h4v-3H5z" stroke="#263012" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Print Slip
              </button>
              <button className="th-btn-outline" onClick={() => setResult(null)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 2l10 10M12 2L2 12" stroke="#263012" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
